// src/routes/AppRouter.tsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../hooks/hooks"; // Adjust the path to your `hooks.ts` file
import LoginModal from "../components/AuthModals/LoginModal";
import SignUpModal from "../components/AuthModals/SignUpModal";
import ForgotPasswordModal from "../components/AuthModals/ResetPasswordModal";
import ResetPasswordResponseModal from "../components/AuthModals/ResetPasswordResponseModal";
import NewPasswordModal from "../components/AuthModals/NewPasswordModal";
import Homepage from "../pages/Homepage";
import ViewAll from "../pages/Homepage/ViewAll";
import Account from "../pages/Account";

// import Profile from "../pages/Profile";
import Navbar from "../components/Navbar";
import Data from "../pages/Products/Data";
import Airtime from "../pages/Products/Airtime";
import Electricity from "../pages/Products/Electricity";
import Cable from "../pages/Products/Cable";
import GiftCard from "../pages/Products/GiftCard";
import GameCard from "../pages/Products/GameCard";
import DirectTopUp from "../pages/Products/DirectTopUp";
import DGCoin from "../pages/Products/DGCoin";
import Cart from "../pages/Cart";
import Support from "../pages/Support";
import SearchResults from "../pages/SearchResults";
import { fetchCurrentUser } from "../features/store/authSlice";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userLocation = useAppSelector((state) => state.locale.userLocation);
  const { data: appData, loading: appDataLoading } = useAppSelector(
    (state) => state.appData,
  );
  const isLocationLoading =
    userLocation.loading ||
    !userLocation.attempted ||
    (appDataLoading && Object.keys(appData).length === 0);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {isLocationLoading ? (
          <div className="min-h-[calc(100vh-80px)] px-4 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-5 w-5 text-indigo-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
              </svg>
              <p className="font-satoshi text-base text-gray-700 dark:text-gray-200">
                Personalizing your experience
              </p>
            </div>
          </div>
        ) : (
          children
        )}
      </main>
      {/* <LoginModal /> */}
      {/* Global Modals */}
      <LoginModal />
      <SignUpModal />
      <ForgotPasswordModal />
      <ResetPasswordResponseModal />
      <NewPasswordModal />
    </div>
  );
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user } = useAppSelector((state) => state.auth);
  return user ? <>{children}</> : <Navigate to="/" />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  return null;
};

const ProtectedRoutes = () => (
  <Routes>
    <Route path="" element={<Account />} />
    {/* <Route path="*" element={<NotFound />} /> */}
  </Routes>
);

const RouteChangeListener = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);

  React.useEffect(() => {
    if (token) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, token, location.pathname, location.search]);

  return null;
};

const AppRouter: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <RouteChangeListener />
        <Routes>
          <Route path="/" element={<Homepage />} />

          <Route path="/cart" element={<Cart />} />
          {/*Used for testing purpose*/}
          <Route path="/account" element={<Account />} />
          <Route
            path="/order-history"
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/*"
            element={
              <ProtectedRoute>
                <ProtectedRoutes />
              </ProtectedRoute>
            }
          />

          <Route path="/products">
            <Route path="data/*" element={<Data />} />
            <Route path="airtime/*" element={<Airtime />} />
            <Route path="electric/*" element={<Electricity />} />
            <Route path="cable/*" element={<Cable />} />
            <Route path="gift_cards/*" element={<GiftCard />} />
            <Route path="dg_coins/*" element={<DGCoin />} />
            <Route path="game_cards/*" element={<GameCard />} />
            <Route path="topup/*" element={<DirectTopUp />} />
          </Route>
          <Route path="/view-all/*" element={<ViewAll />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/support" element={<Support />} />

          {/* <Route path="*" element={<NotFound />} /> */}
        </Routes>
      </Layout>
    </Router>
  );
};

export default AppRouter;